/**
 * Vendor detail — phone row, call, endorse once (optimistic, toggleable
 * locally) + count, resident notes. Wired: GET /api/vendors/:id + POST
 * /api/vendors/:id/endorse. Port of VendorDetail in
 * design/source/screens-vendor-issue.jsx.
 */
import { useLocalSearchParams } from 'expo-router';
import React, { useState } from 'react';
import { Linking, View } from 'react-native';

import { radius, space } from '@bero/tokens';
import { Icon } from '@/components/gate/icons';
import { ArchAvatar, CardSkeletons, goBack } from '@/features/community/bits';
import { endorseVendor, fetchVendor, useFetch, vendorCatLabel } from '@/features/community/lib';
import { useT } from '@/i18n';
import { useTheme } from '@/theme';
import { AppText, Button, EmptyState, IconButton, Screen, SectionLabel, TopBar } from '@/ui';

function callNumber(phone: string) {
  const clean = phone.replace(/[^+\d]/g, '');
  if (clean) void Linking.openURL(`tel:${clean}`).catch(() => {});
}

export default function VendorDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { t } = useT();
  const { colors } = useTheme();
  const { data: vendor, loading, error, refetch, mutate } = useFetch(() => fetchVendor(String(id)), [id]);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const toggleEndorse = async () => {
    if (!vendor || busy) return;
    const prev = vendor;
    setBusy(true);
    setNotice(null);
    mutate(() => ({ ...prev, endorsed: !prev.endorsed, endorsements: Math.max(0, prev.endorsements + (prev.endorsed ? -1 : 1)) }));
    try {
      await endorseVendor(prev.id);
    } catch {
      mutate(() => prev);
      setNotice(t('vendor_endorse_error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <View style={{ flex: 1, backgroundColor: colors.canvas0 }}>
      <TopBar title={t('vendor_detail_title')} onBack={goBack} />
      {loading ? (
        <Screen padded style={{ paddingTop: space.s4 }}>
          <CardSkeletons count={1} />
        </Screen>
      ) : error || !vendor ? (
        <Screen padded>
          <EmptyState
            icon={<Icon name="phone" size={40} color={colors.textTertiary} />}
            title={t('vendor_unavailable_title')}
            body={t('vendor_unavailable_body')}
            cta={t('retry')}
            onCta={() => void refetch()}
          />
        </Screen>
      ) : (
        <Screen padded style={{ paddingTop: space.s4 }}>
          <View style={{ alignItems: 'center' }}>
            <ArchAvatar name={vendor.name || '?'} size={64} tone="filled" />
            <AppText variant="headingLg" style={{ marginTop: space.s3 }}>{vendor.name}</AppText>
            <AppText variant="bodyMd" tone="tertiary" style={{ marginTop: 4 }}>{vendorCatLabel(vendor.category, t)}</AppText>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: space.s2 }}>
              <Icon name="check-circle" size={15} color={colors.success500} />
              <AppText variant="bodyMd" tone="secondary">{t('vendor_endorsements_count', { n: vendor.endorsements })}</AppText>
            </View>
          </View>

          <View style={{ flexDirection: 'row', gap: space.s2, marginTop: space.s6 }}>
            <Button
              title={t('vd_call')}
              style={{ flex: 1 }}
              disabled={!vendor.phone}
              onPress={() => vendor.phone && callNumber(vendor.phone)}
            />
            <Button
              title={vendor.endorsed ? t('vendor_endorsed') : t('vendor_endorse')}
              kind="secondary"
              style={{ flex: 1 }}
              loading={busy}
              onPress={() => void toggleEndorse()}
            />
          </View>

          {vendor.phone ? (
            <View style={{
              backgroundColor: colors.canvas1, borderRadius: radius.lg, padding: space.s4,
              marginTop: space.s4, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <AppText variant="bodySm" tone="tertiary">{t('pinfo_phone')}</AppText>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s2 }}>
                <AppText variant="bodyMd" weight="500" mono>{vendor.phone}</AppText>
                <IconButton label={`Call ${vendor.name}`} onPress={() => callNumber(vendor.phone)} size={32}>
                  <Icon name="phone" size={16} color={colors.primary500} />
                </IconButton>
              </View>
            </View>
          ) : null}

          {vendor.note ? (
            <>
              <SectionLabel style={{ marginTop: space.s6 }}>{t('vendor_resident_notes')}</SectionLabel>
              <AppText variant="bodyLg">{vendor.note}</AppText>
            </>
          ) : null}

          {notice ? <AppText variant="bodySm" tone="danger" style={{ marginTop: space.s4 }}>{notice}</AppText> : null}
        </Screen>
      )}
    </View>
  );
}
